/**
 * CV Section Names Translations
 * This file contains translations for CV section names in all supported languages
 */

export interface CVSectionTranslations {
  aboutMe: string
  experiences: string
  education: string
  skills: string
  technicalSkills: string
  softSkills: string
  languages: string
  contact: string
  websites: string
  professionalJourney: string
  careerHighlights: string
  // Business Web Template specific
  hiIm: string
  getInTouch: string
  degrees: string
  channels: string
  technologiesDescription: string
  letsConnect: string
  openToOpportunities: string
  email: string
  phone: string
  connectWithMe: string
  saveContact: string
  scanToSaveContact: string
  createdWith: string
  noDataAvailable: string
  noDataDescription: string
  // Custom Section
  customSection?: string
  present?: string
  // Additional sections / personal details (fall back to English when missing)
  certificates?: string
  hobbies?: string
  drivingLicense?: string
  dateOfBirth?: string
  location?: string
  linkedin?: string
  // Studio web template: the hero badges (fall back to English when missing)
  yearsExperience?: string
  currentlyAt?: string
  /** The `native` step of the language-level scale (NATIVE_SPEAKER below) */
  nativeSpeaker?: string
}

/**
 * The `native` language level in each CV language. Kept apart from the blocks
 * above so one list covers every language; CEFR codes need no translation.
 */
const NATIVE_SPEAKER: Record<string, string> = {
  cs: 'Rodilý mluvčí', en: 'Native speaker', de: 'Muttersprache', fr: 'Langue maternelle', es: 'Nativo',
  it: 'Madrelingua', pt: 'Nativo', pl: 'Język ojczysty', nl: 'Moedertaal', ru: 'Родной язык',
  uk: 'Рідна мова', sk: 'Materinský jazyk', hu: 'Anyanyelv', ro: 'Limbă maternă', bg: 'Роден език',
  hr: 'Materinski jezik', sr: 'Матерњи језик', sl: 'Materni jezik', da: 'Modersmål', sv: 'Modersmål',
  no: 'Morsmål', fi: 'Äidinkieli', et: 'Emakeel', lv: 'Dzimtā valoda', lt: 'Gimtoji kalba',
  el: 'Μητρική γλώσσα', tr: 'Ana dil', zh: '母语', ja: '母語', ko: '모국어',
  th: 'ภาษาแม่', vi: 'Tiếng mẹ đẻ', id: 'Bahasa ibu', ms: 'Bahasa ibunda', hi: 'मातृभाषा',
  bn: 'মাতৃভাষা', ur: 'مادری زبان', ar: 'اللغة الأم', he: 'שפת אם', fa: 'زبان مادری',
  sw: 'Lugha ya mama', af: 'Moedertaal', am: 'የአፍ መፍቻ ቋንቋ', is: 'Móðurmál', ga: 'Teanga dhúchais',
  mt: 'Lingwa materna', sq: 'Gjuha amtare', mk: 'Мајчин јазик', bs: 'Maternji jezik', cy: 'Iaith gyntaf',
  eu: 'Ama-hizkuntza', ca: 'Llengua materna', gl: 'Lingua materna',
}

export const CV_SECTION_TRANSLATIONS: Record<string, CVSectionTranslations> = {
  // Czech
  cs: {
    aboutMe: 'O mně',
    experiences: 'Pracovní zkušenosti',
    education: 'Vzdělání',
    skills: 'Dovednosti',
    technicalSkills: 'Technické dovednosti',
    softSkills: 'Osobní dovednosti',
    languages: 'Jazyky',
    contact: 'Kontakt',
    websites: 'Odkazy',
    professionalJourney: 'Profesní cesta',
    careerHighlights: 'Hlavní úspěchy a milníky kariéry',
    hiIm: 'Ahoj, jsem',
    getInTouch: 'Napište mi',
    degrees: 'Tituly',
    channels: 'Kanály',
    technologiesDescription: 'Technologie a kompetence, které nabízím',
    letsConnect: 'Pojďme se spojit',
    openToOpportunities: 'Rád probírám nové příležitosti',
    email: 'Email',
    phone: 'Telefon',
    connectWithMe: 'Spojte se se mnou',
    saveContact: 'Uložit kontakt',
    scanToSaveContact: 'Naskenujte pro uložení kontaktu',
    createdWith: 'Vytvořeno pomocí',
    noDataAvailable: 'Žádná data k dispozici',
    noDataDescription: 'Prosím přidejte své informace pro zobrazení šablony.',
    customSection: 'Vlastní sekce',
    present: 'Současnost',
    certificates: 'Certifikáty a kurzy',
    yearsExperience: 'Let praxe',
    currentlyAt: 'Aktuálně v',
    hobbies: 'Zájmy',
    drivingLicense: 'Řidičský průkaz',
    dateOfBirth: 'Datum narození',
    location: 'Bydliště',
    linkedin: 'LinkedIn',
  },

  // English
  en: {
    aboutMe: 'About Me',
    experiences: 'Work Experience',
    education: 'Education',
    skills: 'Skills',
    technicalSkills: 'Technical Skills',
    softSkills: 'Soft Skills',
    languages: 'Languages',
    contact: 'Contact',
    websites: 'Links',
    professionalJourney: 'Professional Journey',
    careerHighlights: 'My career highlights and achievements',
    hiIm: 'Hi, I\'m',
    getInTouch: 'Get in Touch',
    degrees: 'Degrees',
    channels: 'Channels',
    technologiesDescription: 'Technologies and competencies I bring to the table',
    letsConnect: 'Let\'s Connect',
    openToOpportunities: 'I\'m always open to discussing new opportunities',
    email: 'Email',
    phone: 'Phone',
    connectWithMe: 'Connect with me',
    saveContact: 'Save Contact',
    scanToSaveContact: 'Scan to save contact',
    createdWith: 'Created with',
    noDataAvailable: 'No Data Available',
    noDataDescription: 'Please add your information to see the business web template.',
    customSection: 'Custom Section',
    present: 'Present',
    certificates: 'Certificates & Courses',
    yearsExperience: 'Years of experience',
    currentlyAt: 'Currently at',
    hobbies: 'Interests',
    drivingLicense: 'Driving licence',
    dateOfBirth: 'Date of birth',
    location: 'Location',
    linkedin: 'LinkedIn',
  },

  // German
  de: {
    aboutMe: 'Über mich',
    experiences: 'Berufserfahrung',
    education: 'Ausbildung',
    skills: 'Fähigkeiten',
    technicalSkills: 'Technische Fähigkeiten',
    softSkills: 'Soziale Kompetenzen',
    languages: 'Sprachen',
    contact: 'Kontakt',
    websites: 'Links',
    professionalJourney: 'Beruflicher Werdegang',
    careerHighlights: 'Meine Karriere-Highlights und Erfolge',
    hiIm: 'Hallo, ich bin',
    getInTouch: 'Kontaktieren Sie mich',
    degrees: 'Abschlüsse',
    channels: 'Kanäle',
    technologiesDescription: 'Technologien und Kompetenzen, die ich mitbringe',
    letsConnect: 'Lassen Sie uns vernetzen',
    openToOpportunities: 'Ich bin immer offen für neue Möglichkeiten',
    email: 'E-Mail',
    phone: 'Telefon',
    connectWithMe: 'Vernetzen Sie sich mit mir',
    saveContact: 'Kontakt speichern',
    scanToSaveContact: 'Scannen zum Speichern',
    createdWith: 'Erstellt mit',
    noDataAvailable: 'Keine Daten verfügbar',
    noDataDescription: 'Bitte fügen Sie Ihre Informationen hinzu, um die Vorlage anzuzeigen.',
    customSection: 'Benutzerdefinierter Abschnitt',
    present: 'Gegenwart',
    certificates: 'Zertifikate & Kurse',
    yearsExperience: 'Jahre Erfahrung',
    currentlyAt: 'Aktuell bei',
    hobbies: 'Interessen',
    drivingLicense: 'Führerschein',
    dateOfBirth: 'Geburtsdatum',
    location: 'Wohnort',
    linkedin: 'LinkedIn',
  },

  // French
  fr: {
    aboutMe: 'À propos de moi',
    experiences: 'Expérience professionnelle',
    education: 'Formation',
    skills: 'Compétences',
    technicalSkills: 'Compétences techniques',
    softSkills: 'Compétences interpersonnelles',
    languages: 'Langues',
    contact: 'Contact',
    websites: 'Liens',
    professionalJourney: 'Parcours professionnel',
    careerHighlights: 'Points forts et réalisations de ma carrière',
    hiIm: 'Bonjour, je suis',
    getInTouch: 'Contactez-moi',
    degrees: 'Diplômes',
    channels: 'Canaux',
    technologiesDescription: 'Technologies et compétences que j\'apporte',
    letsConnect: 'Connectons-nous',
    openToOpportunities: 'Je suis toujours ouvert à discuter de nouvelles opportunités',
    email: 'Email',
    phone: 'Téléphone',
    connectWithMe: 'Connectez-vous avec moi',
    saveContact: 'Enregistrer le contact',
    scanToSaveContact: 'Scanner pour enregistrer',
    createdWith: 'Créé avec',
    noDataAvailable: 'Aucune donnée disponible',
    noDataDescription: 'Veuillez ajouter vos informations pour voir le modèle.',
    customSection: 'Section personnalisée',
    present: 'Présent',
    certificates: 'Certifications et formations',
    yearsExperience: 'Années d\'expérience',
    currentlyAt: 'Actuellement chez',
    hobbies: 'Centres d\'intérêt',
    drivingLicense: 'Permis de conduire',
    dateOfBirth: 'Date de naissance',
    location: 'Localisation',
    linkedin: 'LinkedIn',
  },

  // Spanish
  es: {
    aboutMe: 'Sobre mí',
    experiences: 'Experiencia laboral',
    education: 'Educación',
    skills: 'Habilidades',
    technicalSkills: 'Habilidades técnicas',
    softSkills: 'Habilidades blandas',
    languages: 'Idiomas',
    contact: 'Contacto',
    websites: 'Enlaces',
    professionalJourney: 'Trayectoria profesional',
    careerHighlights: 'Mis logros y éxitos profesionales',
    hiIm: 'Hola, soy',
    getInTouch: 'Ponte en contacto',
    degrees: 'Títulos',
    channels: 'Canales',
    technologiesDescription: 'Tecnologías y competencias que aporto',
    letsConnect: 'Conectemos',
    openToOpportunities: 'Siempre estoy abierto a discutir nuevas oportunidades',
    email: 'Correo',
    phone: 'Teléfono',
    connectWithMe: 'Conéctate conmigo',
    saveContact: 'Guardar contacto',
    scanToSaveContact: 'Escanea para guardar',
    createdWith: 'Creado con',
    noDataAvailable: 'No hay datos disponibles',
    noDataDescription: 'Por favor agrega tu información para ver la plantilla.',
    customSection: 'Sección personalizada',
    present: 'Presente',
    certificates: 'Certificados y cursos',
    yearsExperience: 'Años de experiencia',
    currentlyAt: 'Actualmente en',
    hobbies: 'Intereses',
    drivingLicense: 'Permiso de conducir',
    dateOfBirth: 'Fecha de nacimiento',
    location: 'Ubicación',
    linkedin: 'LinkedIn',
  },

  // Italian
  it: {
    aboutMe: 'Su di me',
    experiences: 'Esperienza lavorativa',
    education: 'Istruzione',
    skills: 'Competenze',
    technicalSkills: 'Competenze tecniche',
    softSkills: 'Competenze trasversali',
    languages: 'Lingue',
    contact: 'Contatto',
    websites: 'Collegamenti',
    professionalJourney: 'Percorso professionale',
    careerHighlights: 'I miei traguardi e successi',
    hiIm: 'Ciao, sono',
    getInTouch: 'Contattami',
    degrees: 'Titoli',
    channels: 'Canali',
    technologiesDescription: 'Tecnologie e competenze che porto',
    letsConnect: 'Connettiamoci',
    openToOpportunities: 'Sono sempre aperto a discutere nuove opportunità',
    email: 'Email',
    phone: 'Telefono',
    connectWithMe: 'Connettiti con me',
    saveContact: 'Salva contatto',
    scanToSaveContact: 'Scansiona per salvare',
    createdWith: 'Creato con',
    noDataAvailable: 'Nessun dato disponibile',
    noDataDescription: 'Aggiungi le tue informazioni per vedere il modello.',
    certificates: 'Certificazioni e corsi',
    yearsExperience: 'Anni di esperienza',
    currentlyAt: 'Attualmente presso',
    hobbies: 'Interessi',
    drivingLicense: 'Patente di guida',
    dateOfBirth: 'Data di nascita',
    location: 'Località',
    linkedin: 'LinkedIn',
  },

  // Portuguese
  pt: {
    aboutMe: 'Sobre mim',
    experiences: 'Experiência profissional',
    education: 'Educação',
    skills: 'Competências',
    technicalSkills: 'Competências técnicas',
    softSkills: 'Competências interpessoais',
    languages: 'Idiomas',
    contact: 'Contacto',
    websites: 'Links',
    professionalJourney: 'Jornada profissional',
    careerHighlights: 'Meus destaques e conquistas na carreira',
    hiIm: 'Olá, eu sou',
    getInTouch: 'Entre em contato',
    degrees: 'Graus',
    channels: 'Canais',
    technologiesDescription: 'Tecnologias e competências que trago',
    letsConnect: 'Vamos nos conectar',
    openToOpportunities: 'Estou sempre aberto a discutir novas oportunidades',
    email: 'Email',
    phone: 'Telefone',
    connectWithMe: 'Conecte-se comigo',
    saveContact: 'Guardar contacto',
    scanToSaveContact: 'Digitalize para salvar',
    createdWith: 'Criado com',
    noDataAvailable: 'Sem dados disponíveis',
    noDataDescription: 'Por favor, adicione suas informações para ver o modelo.',
    certificates: 'Certificados e cursos',
    yearsExperience: 'Anos de experiência',
    currentlyAt: 'Atualmente na',
    hobbies: 'Interesses',
    drivingLicense: 'Carta de condução',
    dateOfBirth: 'Data de nascimento',
    location: 'Localização',
    linkedin: 'LinkedIn',
  },

  // Portuguese (Brazil)
  'pt-BR': {
    aboutMe: 'Sobre mim',
    experiences: 'Experiência profissional',
    education: 'Educação',
    skills: 'Competências',
    technicalSkills: 'Competências técnicas',
    softSkills: 'Competências interpessoais',
    languages: 'Idiomas',
    contact: 'Contato',
    websites: 'Links',
    professionalJourney: 'Professional Journey',
    careerHighlights: 'Hlavní úspěchy a milníky kariéry',
    hiIm: 'Oi, eu sou',
    getInTouch: 'Entre em contato',
    degrees: 'Diplomas',
    channels: 'Canais',
    technologiesDescription: 'Tecnologias e competências que ofereço',
    letsConnect: 'Vamos nos conectar',
    openToOpportunities: 'Estou sempre aberto para discutir novas oportunidades',
    email: 'Email',
    phone: 'Telefone',
    connectWithMe: 'Conecte-se comigo',
    saveContact: 'Salvar contato',
    scanToSaveContact: 'Escaneie para salvar',
    createdWith: 'Criado com',
    noDataAvailable: 'Sem dados disponíveis',
    noDataDescription: 'Por favor adicione suas informações para ver o template.',
  },

  // Polish
  pl: {
    aboutMe: 'O mnie',
    experiences: 'Doświadczenie zawodowe',
    education: 'Wykształcenie',
    skills: 'Umiejętności',
    technicalSkills: 'Umiejętności techniczne',
    softSkills: 'Umiejętności miękkie',
    languages: 'Języki',
    contact: 'Kontakt',
    websites: 'Linki',
    professionalJourney: 'Ścieżka zawodowa',
    careerHighlights: 'Moje osiągnięcia i sukcesy zawodowe',
    hiIm: 'Cześć, jestem',
    getInTouch: 'Skontaktuj się',
    degrees: 'Stopnie',
    channels: 'Kanały',
    technologiesDescription: 'Technologie i kompetencje, które wnoszę',
    letsConnect: 'Połączmy się',
    openToOpportunities: 'Zawsze jestem otwarty na rozmowy o nowych możliwościach',
    email: 'Email',
    phone: 'Telefon',
    connectWithMe: 'Połącz się ze mną',
    saveContact: 'Zapisz kontakt',
    scanToSaveContact: 'Skanuj aby zapisać',
    createdWith: 'Stworzone z',
    noDataAvailable: 'Brak danych',
    noDataDescription: 'Dodaj swoje informacje, aby zobaczyć szablon.',
    certificates: 'Certyfikaty i kursy',
    yearsExperience: 'Lata doświadczenia',
    currentlyAt: 'Obecnie w',
    hobbies: 'Zainteresowania',
    drivingLicense: 'Prawo jazdy',
    dateOfBirth: 'Data urodzenia',
    location: 'Miejscowość',
    linkedin: 'LinkedIn',
  },

  // Dutch
  nl: {
    aboutMe: 'Over mij',
    experiences: 'Werkervaring',
    education: 'Opleiding',
    skills: 'Vaardigheden',
    technicalSkills: 'Technische vaardigheden',
    softSkills: 'Sociale vaardigheden',
    languages: 'Talen',
    contact: 'Contact',
    websites: 'Links',
    professionalJourney: 'Professioneel traject',
    careerHighlights: 'Mijn carrièrehoogtepunten en prestaties',
    hiIm: 'Hallo, ik ben',
    getInTouch: 'Neem contact op',
    degrees: 'Graden',
    channels: 'Kanalen',
    technologiesDescription: 'Technologieën en competenties die ik inbreng',
    letsConnect: 'Laten we verbinden',
    openToOpportunities: 'Ik sta altijd open om nieuwe kansen te bespreken',
    email: 'Email',
    phone: 'Telefoon',
    connectWithMe: 'Maak contact met mij',
    saveContact: 'Contact opslaan',
    scanToSaveContact: 'Scan om op te slaan',
    createdWith: 'Gemaakt met',
    noDataAvailable: 'Geen gegevens beschikbaar',
    noDataDescription: 'Voeg uw informatie toe om de sjabloon te zien.',
    certificates: 'Certificaten & cursussen',
    yearsExperience: 'Jaar ervaring',
    currentlyAt: 'Momenteel bij',
    hobbies: 'Interesses',
    drivingLicense: 'Rijbewijs',
    dateOfBirth: 'Geboortedatum',
    location: 'Woonplaats',
    linkedin: 'LinkedIn',
  },

  // Russian
  ru: {
    aboutMe: 'Обо мне',
    experiences: 'Опыт работы',
    education: 'Образование',
    skills: 'Навыки',
    technicalSkills: 'Технические навыки',
    softSkills: 'Межличностные навыки',
    languages: 'Языки',
    contact: 'Контакт',
    websites: 'Ссылки',
    professionalJourney: 'Профессиональный путь',
    careerHighlights: 'Мои карьерные достижения и успехи',
    hiIm: 'Привет, я',
    getInTouch: 'Свяжитесь со мной',
    degrees: 'Степени',
    channels: 'Каналы',
    technologiesDescription: 'Технологии и компетенции, которые я предлагаю',
    letsConnect: 'Давайте свяжемся',
    openToOpportunities: 'Я всегда открыт для обсуждения новых возможностей',
    email: 'Эл. почта',
    phone: 'Телефон',
    connectWithMe: 'Свяжитесь со мной',
    saveContact: 'Сохранить контакт',
    scanToSaveContact: 'Сканировать для сохранения',
    createdWith: 'Создано с помощью',
    noDataAvailable: 'Нет доступных данных',
    noDataDescription: 'Пожалуйста, добавьте свою информацию для просмотра шаблона.',
    certificates: 'Сертификаты и курсы',
    yearsExperience: 'Лет опыта',
    currentlyAt: 'Сейчас в',
    hobbies: 'Интересы',
    drivingLicense: 'Водительские права',
    dateOfBirth: 'Дата рождения',
    location: 'Местоположение',
    linkedin: 'LinkedIn',
  },

  // Ukrainian
  uk: {
    aboutMe: 'Про мене',
    experiences: 'Досвід роботи',
    education: 'Освіта',
    skills: 'Навички',
    technicalSkills: 'Технічні навички',
    softSkills: 'Міжособистісні навички',
    languages: 'Мови',
    contact: 'Контакт',
    websites: 'Посилання',
    professionalJourney: 'Професійний шлях',
    careerHighlights: 'Мої карєрні досягнення та успіхи',
    hiIm: 'Привіт, я',
    getInTouch: 'Зв\'яжіться',
    degrees: 'Ступені',
    channels: 'Канали',
    technologiesDescription: 'Технології та компетенції, які я пропоную',
    letsConnect: 'Давайте зв\'яжемося',
    openToOpportunities: 'Я завжди відкритий для обговорення нових можливостей',
    email: 'Ел. пошта',
    phone: 'Телефон',
    connectWithMe: 'Зв\'яжіться зі мною',
    saveContact: 'Зберегти контакт',
    scanToSaveContact: 'Сканувати для збереження',
    createdWith: 'Створено за допомогою',
    noDataAvailable: 'Немає доступних даних',
    noDataDescription: 'Будь ласка, додайте свою інформацію для перегляду шаблону.',
    certificates: 'Сертифікати та курси',
    yearsExperience: 'Років досвіду',
    currentlyAt: 'Зараз у',
    hobbies: 'Інтереси',
    drivingLicense: 'Водійське посвідчення',
    dateOfBirth: 'Дата народження',
    location: 'Місцезнаходження',
    linkedin: 'LinkedIn',
  },

  // Slovak
  sk: {
    aboutMe: 'O mne',
    experiences: 'Pracovné skúsenosti',
    education: 'Vzdelanie',
    skills: 'Zručnosti',
    technicalSkills: 'Technické zručnosti',
    softSkills: 'Osobné zručnosti',
    languages: 'Jazyky',
    contact: 'Kontakt',
    websites: 'Odkazy',
    professionalJourney: 'Profesijná cesta',
    careerHighlights: 'Moje kariérne úspechy a úspechy',
    hiIm: 'Ahoj, som',
    getInTouch: 'Napíšte mi',
    degrees: 'Tituly',
    channels: 'Kanály',
    technologiesDescription: 'Technológie a kompetencie, ktoré ponúkam',
    letsConnect: 'Spojme sa',
    openToOpportunities: 'Rád preberám nové príležitosti',
    email: 'Email',
    phone: 'Telefón',
    connectWithMe: 'Spojte sa so mnou',
    saveContact: 'Uložiť kontakt',
    scanToSaveContact: 'Naskenujte pre uloženie',
    createdWith: 'Vytvorené pomocou',
    noDataAvailable: 'Žiadne dáta k dispozícii',
    noDataDescription: 'Prosím pridajte svoje informácie pre zobrazenie šablóny.',
    certificates: 'Certifikáty a kurzy',
    yearsExperience: 'Rokov praxe',
    currentlyAt: 'Aktuálne v',
    hobbies: 'Záujmy',
    drivingLicense: 'Vodičský preukaz',
    dateOfBirth: 'Dátum narodenia',
    location: 'Bydlisko',
    linkedin: 'LinkedIn',
  },

  // Hungarian
  hu: {
    aboutMe: 'Rólam',
    experiences: 'Munkatapasztalat',
    education: 'Tanulmányok',
    skills: 'Készségek',
    technicalSkills: 'Technikai készségek',
    softSkills: 'Szociális készségek',
    languages: 'Nyelvek',
    contact: 'Kapcsolat',
    websites: 'Hivatkozások',
    professionalJourney: 'Szakmai út',
    careerHighlights: 'Karrierem kiemelkedő eredményei',
    hiIm: 'Szia, én vagyok',
    getInTouch: 'Lépj kapcsolatba',
    degrees: 'Fokozatok',
    channels: 'Csatornák',
    technologiesDescription: 'Technológiák és kompetenciák, amiket hozok',
    letsConnect: 'Kapcsolódjunk',
    openToOpportunities: 'Mindig nyitott vagyok új lehetőségek megbeszélésére',
    email: 'Email',
    phone: 'Telefon',
    connectWithMe: 'Kapcsolódj velem',
    saveContact: 'Kapcsolat mentése',
    scanToSaveContact: 'Szkennelés mentéshez',
    createdWith: 'Készült ezzel',
    noDataAvailable: 'Nincs elérhető adat',
    noDataDescription: 'Kérjük, adja meg az információit a sablon megtekintéséhez.',
    certificates: 'Tanúsítványok és képzések',
    yearsExperience: 'Év tapasztalat',
    currentlyAt: 'Jelenleg',
    hobbies: 'Érdeklődési körök',
    drivingLicense: 'Jogosítvány',
    dateOfBirth: 'Születési dátum',
    location: 'Lakhely',
    linkedin: 'LinkedIn',
  },

  // Romanian
  ro: {
    aboutMe: 'Despre mine',
    experiences: 'Experiență profesională',
    education: 'Educație',
    skills: 'Competențe',
    technicalSkills: 'Competențe tehnice',
    softSkills: 'Competențe sociale',
    languages: 'Limbi',
    contact: 'Contact',
    websites: 'Link-uri',
    professionalJourney: 'Parcurs profesional',
    careerHighlights: 'Realizările și succesele mele în carieră',
    hiIm: 'Bună, sunt',
    getInTouch: 'Ia legătura',
    degrees: 'Grade',
    channels: 'Canale',
    technologiesDescription: 'Tehnologii și competențe pe care le aduc',
    letsConnect: 'Hai să ne conectăm',
    openToOpportunities: 'Sunt mereu deschis să discut noi oportunități',
    email: 'Email',
    phone: 'Telefon',
    connectWithMe: 'Conectează-te cu mine',
    saveContact: 'Salvează contactul',
    scanToSaveContact: 'Scanează pentru salvare',
    createdWith: 'Creat cu',
    noDataAvailable: 'Nu sunt date disponibile',
    noDataDescription: 'Vă rugăm să adăugați informațiile pentru a vedea șablonul.',
    certificates: 'Certificări și cursuri',
    yearsExperience: 'Ani de experiență',
    currentlyAt: 'În prezent la',
    hobbies: 'Interese',
    drivingLicense: 'Permis de conducere',
    dateOfBirth: 'Data nașterii',
    location: 'Localitate',
    linkedin: 'LinkedIn',
  },

  // Bulgarian
  bg: {
    aboutMe: 'За мен',
    experiences: 'Професионален опит',
    education: 'Образование',
    skills: 'Умения',
    technicalSkills: 'Технически умения',
    softSkills: 'Меки умения',
    languages: 'Езици',
    contact: 'Контакт',
    websites: 'Връзки',
    professionalJourney: 'Професионален път',
    careerHighlights: 'Моите кариерни постижения и успехи',
    hiIm: 'Здравей, аз съм',
    getInTouch: 'Свържете се',
    degrees: 'Степени',
    channels: 'Канали',
    technologiesDescription: 'Технологии и компетенции, които предлагам',
    letsConnect: 'Нека се свържем',
    openToOpportunities: 'Винаги съм отворен за обсъждане на нови възможности',
    email: 'Имейл',
    phone: 'Телефон',
    connectWithMe: 'Свържете се с мен',
    saveContact: 'Запази контакт',
    scanToSaveContact: 'Сканирай за запазване',
    createdWith: 'Създадено с',
    noDataAvailable: 'Няма налични данни',
    noDataDescription: 'Моля, добавете информацията си, за да видите шаблона.',
  },

  // Croatian
  hr: {
    aboutMe: 'O meni',
    experiences: 'Radno iskustvo',
    education: 'Obrazovanje',
    skills: 'Vještine',
    technicalSkills: 'Tehničke vještine',
    softSkills: 'Meke vještine',
    languages: 'Jezici',
    contact: 'Kontakt',
    websites: 'Poveznice',
    professionalJourney: 'Profesionalni put',
    careerHighlights: 'Moji karijerni uspjesi i dostignuća',
    hiIm: 'Bok, ja sam',
    getInTouch: 'Javite se',
    degrees: 'Stupnjevi',
    channels: 'Kanali',
    technologiesDescription: 'Tehnologije i kompetencije koje donosim',
    letsConnect: 'Povežimo se',
    openToOpportunities: 'Uvijek sam otvoren za razgovor o novim prilikama',
    email: 'Email',
    phone: 'Telefon',
    connectWithMe: 'Povežite se sa mnom',
    saveContact: 'Spremi kontakt',
    scanToSaveContact: 'Skeniraj za spremanje',
    createdWith: 'Stvoreno s',
    noDataAvailable: 'Nema dostupnih podataka',
    noDataDescription: 'Molimo dodajte svoje informacije za prikaz predloška.',
  },

  // Serbian
  sr: {
    aboutMe: 'О мени',
    experiences: 'Радно искуство',
    education: 'Образовање',
    skills: 'Вештине',
    technicalSkills: 'Техничке вештине',
    softSkills: 'Меке вештине',
    languages: 'Језици',
    contact: 'Контакт',
    websites: 'Везе',
    professionalJourney: 'Професионални пут',
    careerHighlights: 'Моји каријерни успеси и достигнућа',
    hiIm: 'Здраво, ја сам',
    getInTouch: 'Јавите се',
    degrees: 'Степени',
    channels: 'Канали',
    technologiesDescription: 'Технологије и компетенције које доносим',
    letsConnect: 'Повежимо се',
    openToOpportunities: 'Увек сам отворен за разговор о новим приликама',
    email: 'Имејл',
    phone: 'Телефон',
    connectWithMe: 'Повежите се са мном',
    saveContact: 'Сачувај контакт',
    scanToSaveContact: 'Скенирај за чување',
    createdWith: 'Направљено са',
    noDataAvailable: 'Нема доступних података',
    noDataDescription: 'Молимо додајте своје информације за приказ шаблона.',
  },

  // Slovenian
  sl: {
    aboutMe: 'O meni',
    experiences: 'Delovne izkušnje',
    education: 'Izobrazba',
    skills: 'Veščine',
    technicalSkills: 'Tehnične veščine',
    softSkills: 'Mehke veščine',
    languages: 'Jeziki',
    contact: 'Kontakt',
    websites: 'Povezave',
    professionalJourney: 'Poklicna pot',
    careerHighlights: 'Moji karierni vrhunci in dosežki',
    hiIm: 'Pozdravljeni, jaz sem',
    getInTouch: 'Stopite v stik',
    degrees: 'Stopnje',
    channels: 'Kanali',
    technologiesDescription: 'Tehnologije in kompetence, ki jih prinašam',
    letsConnect: 'Povežimo se',
    openToOpportunities: 'Vedno sem odprt za pogovor o novih priložnostih',
    email: 'E-pošta',
    phone: 'Telefon',
    connectWithMe: 'Povežite se z mano',
    saveContact: 'Shrani stik',
    scanToSaveContact: 'Skeniraj za shranitev',
    createdWith: 'Ustvarjeno z',
    noDataAvailable: 'Ni razpoložljivih podatkov',
    noDataDescription: 'Prosimo, dodajte svoje informacije za ogled predloge.',
  },

  // Danish
  da: {
    aboutMe: 'Om mig',
    experiences: 'Arbejdserfaring',
    education: 'Uddannelse',
    skills: 'Færdigheder',
    technicalSkills: 'Tekniske færdigheder',
    softSkills: 'Bløde færdigheder',
    languages: 'Sprog',
    contact: 'Kontakt',
    websites: 'Links',
    professionalJourney: 'Professionel rejse',
    careerHighlights: 'Mine karrierehøjdepunkter og præstationer',
    hiIm: 'Hej, jeg er',
    getInTouch: 'Kontakt mig',
    degrees: 'Grader',
    channels: 'Kanaler',
    technologiesDescription: 'Teknologier og kompetencer jeg bringer',
    letsConnect: 'Lad os forbinde',
    openToOpportunities: 'Jeg er altid åben for at diskutere nye muligheder',
    email: 'Email',
    phone: 'Telefon',
    connectWithMe: 'Forbind med mig',
    saveContact: 'Gem kontakt',
    scanToSaveContact: 'Scan for at gemme',
    createdWith: 'Oprettet med',
    noDataAvailable: 'Ingen data tilgængelig',
    noDataDescription: 'Tilføj venligst dine oplysninger for at se skabelonen.',
  },

  // Swedish
  sv: {
    aboutMe: 'Om mig',
    experiences: 'Arbetslivserfarenhet',
    education: 'Utbildning',
    skills: 'Färdigheter',
    technicalSkills: 'Tekniska färdigheter',
    softSkills: 'Mjuka färdigheter',
    languages: 'Språk',
    contact: 'Kontakt',
    websites: 'Länkar',
    professionalJourney: 'Professionell resa',
    careerHighlights: 'Mina karriärhöjdpunkter och prestationer',
    hiIm: 'Hej, jag är',
    getInTouch: 'Kontakta mig',
    degrees: 'Grader',
    channels: 'Kanaler',
    technologiesDescription: 'Teknologier och kompetenser jag tar med',
    letsConnect: 'Låt oss ansluta',
    openToOpportunities: 'Jag är alltid öppen för att diskutera nya möjligheter',
    email: 'E-post',
    phone: 'Telefon',
    connectWithMe: 'Anslut med mig',
    saveContact: 'Spara kontakt',
    scanToSaveContact: 'Skanna för att spara',
    createdWith: 'Skapad med',
    noDataAvailable: 'Inga data tillgängliga',
    noDataDescription: 'Vänligen lägg till din information för att se mallen.',
  },

  // Norwegian
  no: {
    aboutMe: 'Om meg',
    experiences: 'Arbeidserfaring',
    education: 'Utdanning',
    skills: 'Ferdigheter',
    technicalSkills: 'Tekniske ferdigheter',
    softSkills: 'Myke ferdigheter',
    languages: 'Språk',
    contact: 'Kontakt',
    websites: 'Lenker',
    professionalJourney: 'Profesjonell reise',
    careerHighlights: 'Mine karrierehøydepunkter og prestasjoner',
    hiIm: 'Hei, jeg er',
    getInTouch: 'Ta kontakt',
    degrees: 'Grader',
    channels: 'Kanaler',
    technologiesDescription: 'Teknologier og kompetanser jeg bringer',
    letsConnect: 'La oss koble',
    openToOpportunities: 'Jeg er alltid åpen for å diskutere nye muligheter',
    email: 'E-post',
    phone: 'Telefon',
    connectWithMe: 'Koble med meg',
    saveContact: 'Lagre kontakt',
    scanToSaveContact: 'Skann for å lagre',
    createdWith: 'Laget med',
    noDataAvailable: 'Ingen data tilgjengelig',
    noDataDescription: 'Vennligst legg til informasjonen din for å se malen.',
  },

  // Finnish
  fi: {
    aboutMe: 'Minusta',
    experiences: 'Työkokemus',
    education: 'Koulutus',
    skills: 'Taidot',
    technicalSkills: 'Tekniset taidot',
    softSkills: 'Pehmeät taidot',
    languages: 'Kielet',
    contact: 'Yhteystiedot',
    websites: 'Linkit',
    professionalJourney: 'Ammatillinen matka',
    careerHighlights: 'Urieni kohokohdat ja saavutukset',
    hiIm: 'Hei, olen',
    getInTouch: 'Ota yhteyttä',
    degrees: 'Tutkinnot',
    channels: 'Kanavat',
    technologiesDescription: 'Teknologiat ja osaamiset, jotka tuon',
    letsConnect: 'Otetaan yhteyttä',
    openToOpportunities: 'Olen aina avoin keskustelemaan uusista mahdollisuuksista',
    email: 'Sähköposti',
    phone: 'Puhelin',
    connectWithMe: 'Ota yhteyttä minuun',
    saveContact: 'Tallenna yhteystieto',
    scanToSaveContact: 'Skannaa tallentaaksesi',
    createdWith: 'Luotu sovelluksella',
    noDataAvailable: 'Ei tietoja saatavilla',
    noDataDescription: 'Lisää tietosi nähdäksesi mallin.',
  },

  // Estonian
  et: {
    aboutMe: 'Minust',
    experiences: 'Töökogemus',
    education: 'Haridus',
    skills: 'Oskused',
    technicalSkills: 'Tehnilised oskused',
    softSkills: 'Pehmed oskused',
    languages: 'Keeled',
    contact: 'Kontakt',
    websites: 'Lingid',
    professionalJourney: 'Professionaalne teekond',
    careerHighlights: 'Minu karjääri tippsündmused ja saavutused',
    hiIm: 'Tere, mina olen',
    getInTouch: 'võta ühendust',
    degrees: 'Kraadid',
    channels: 'Kanalid',
    technologiesDescription: 'Tehnoloogiad ja pädevused, mida pakun',
    letsConnect: 'Ühendame',
    openToOpportunities: 'Olen alati avatud uute võimaluste arutamiseks',
    email: 'E-post',
    phone: 'Telefon',
    connectWithMe: 'Ühenda minuga',
    saveContact: 'Salvesta kontakt',
    scanToSaveContact: 'Skaneeri salvestamiseks',
    createdWith: 'Loodud',
    noDataAvailable: 'Andmed pole saadaval',
    noDataDescription: 'Palun lisage oma teave malli vaatamiseks.',
  },

  // Latvian
  lv: {
    aboutMe: 'Par mani',
    experiences: 'Darba pieredze',
    education: 'Izglītība',
    skills: 'Prasmes',
    technicalSkills: 'Tehniskās prasmes',
    softSkills: 'Mīkstās prasmes',
    languages: 'Valodas',
    contact: 'Kontakti',
    websites: 'Saites',
    professionalJourney: 'Profesionālais ceļš',
    careerHighlights: 'Mani karjeras sasniegumi un panākumi',
    hiIm: 'Sveiki, es esmu',
    getInTouch: 'Sazinies',
    degrees: 'Grādi',
    channels: 'Kanāli',
    technologiesDescription: 'Tehnoloģijas un kompetences, ko piedāvāju',
    letsConnect: 'Sazināsimies',
    openToOpportunities: 'Vienmēr esmu atvērts jaunām iespējām',
    email: 'E-pasts',
    phone: 'Tālrunis',
    connectWithMe: 'Sazinies ar mani',
    saveContact: 'Saglabāt kontaktu',
    scanToSaveContact: 'Skenē, lai saglabātu',
    createdWith: 'Izveidots ar',
    noDataAvailable: 'Nav pieejamu datu',
    noDataDescription: 'Lūdzu, pievienojiet savu informāciju, lai redzētu veidni.',
  },

  // Lithuanian
  lt: {
    aboutMe: 'Apie mane',
    experiences: 'Darbo patirtis',
    education: 'Išsilavinimas',
    skills: 'Įgūdžiai',
    technicalSkills: 'Techniniai įgūdžiai',
    softSkills: 'Socialiniai įgūdžiai',
    languages: 'Kalbos',
    contact: 'Kontaktai',
    websites: 'Nuorodos',
    professionalJourney: 'Profesinė kelionė',
    careerHighlights: 'Mano karjeros pasiekimai ir laimėjimai',
    hiIm: 'Sveiki, aš esu',
    getInTouch: 'Susisiekite',
    degrees: 'Laipsniai',
    channels: 'Kanalai',
    technologiesDescription: 'Technologijos ir kompetencijos, kurias siūlau',
    letsConnect: 'Susijunkime',
    openToOpportunities: 'Visada esu atviras naujoms galimybėms',
    email: 'El. paštas',
    phone: 'Telefonas',
    connectWithMe: 'Susisiekite su manimi',
    saveContact: 'Išsaugoti kontaktą',
    scanToSaveContact: 'Nuskaitykite, kad išsaugotumėte',
    createdWith: 'Sukurta su',
    noDataAvailable: 'Nėra duomenų',
    noDataDescription: 'Pridėkite savo informaciją, kad pamatytumėte šabloną.',
  },

  // Greek
  el: {
    aboutMe: 'Σχετικά με εμένα',
    experiences: 'Εργασιακή εμπειρία',
    education: 'Εκπαίδευση',
    skills: 'Δεξιότητες',
    technicalSkills: 'Τεχνικές δεξιότητες',
    softSkills: 'Μαλακές δεξιότητες',
    languages: 'Γλώσσες',
    contact: 'Επικοινωνία',
    websites: 'Σύνδεσμοι',
    professionalJourney: 'Επαγγελματική πορεία',
    careerHighlights: 'Τα καλύτερα σημεία και τα επιτεύγματα της καριέρας μου',
    hiIm: 'Γεια, είμαι',
    getInTouch: 'Επικοινωνήστε',
    degrees: 'Πτυχία',
    channels: 'Κανάλια',
    technologiesDescription: 'Τεχνολογίες και ικανότητες που προσφέρω',
    letsConnect: 'Ας συνδεθούμε',
    openToOpportunities: 'Είμαι πάντα ανοιχτός σε νέες ευκαιρίες',
    email: 'Email',
    phone: 'Τηλέφωνο',
    connectWithMe: 'Συνδεθείτε μαζί μου',
    saveContact: 'Αποθήκευση επαφής',
    scanToSaveContact: 'Σάρωση για αποθήκευση',
    createdWith: 'Δημιουργήθηκε με',
    noDataAvailable: 'Δεν υπάρχουν διαθέσιμα δεδομένα',
    noDataDescription: 'Παρακαλώ προσθέστε τα στοιχεία σας για να δείτε το πρότυπο.',
  },

  // Turkish
  tr: {
    aboutMe: 'Hakkımda',
    experiences: 'İş Deneyimi',
    education: 'Eğitim',
    skills: 'Yetenekler',
    technicalSkills: 'Teknik Yetenekler',
    softSkills: 'Sosyal Yetenekler',
    languages: 'Diller',
    contact: 'İletişim',
    websites: 'Bağlantılar',
    professionalJourney: 'Profesyonel yolculuk',
    careerHighlights: 'Kariyer öne çıkanlarım ve başarılarım',
    hiIm: 'Merhaba, ben',
    getInTouch: 'İletişime geçin',
    degrees: 'Dereceler',
    channels: 'Kanallar',
    technologiesDescription: 'Sunduğum teknolojiler ve yetkinlikler',
    letsConnect: 'Bağlanalım',
    openToOpportunities: 'Yeni fırsatları tartışmaya her zaman açığım',
    email: 'E-posta',
    phone: 'Telefon',
    connectWithMe: 'Benimle bağlantı kurun',
    saveContact: 'Kişiyi kaydet',
    scanToSaveContact: 'Kaydetmek için tarayın',
    createdWith: 'İle oluşturuldu',
    noDataAvailable: 'Veri yok',
    noDataDescription: 'Lütfen şablonu görmek için bilgilerinizi ekleyin.',
  },

  // Chinese
  zh: {
    aboutMe: '关于我',
    experiences: '工作经验',
    education: '教育背景',
    skills: '技能',
    technicalSkills: '技术技能',
    softSkills: '软技能',
    languages: '语言',
    contact: '联系方式',
    websites: '链接',
    professionalJourney: '职业历程',
    careerHighlights: '我的职业亮点与成就',
    hiIm: '嗨，我是',
    getInTouch: '联系我',
    degrees: '学位',
    channels: '渠道',
    technologiesDescription: '我带来的技术和能力',
    letsConnect: '让我们联系',
    openToOpportunities: '我总是乐于讨论新机会',
    email: '电子邮件',
    phone: '电话',
    connectWithMe: '与我联系',
    saveContact: '保存联系人',
    scanToSaveContact: '扫描保存',
    createdWith: '创建于',
    noDataAvailable: '无可用数据',
    noDataDescription: '请添加您的信息以查看模板。',
  },

  // Japanese
  ja: {
    aboutMe: '自己紹介',
    experiences: '職歴',
    education: '学歴',
    skills: 'スキル',
    technicalSkills: '技術スキル',
    softSkills: 'ソフトスキル',
    languages: '言語',
    contact: '連絡先',
    websites: 'リンク',
    professionalJourney: 'プロフェッショナルな歩み',
    careerHighlights: '私のキャリアのハイライトと実績',
    hiIm: 'こんにちは、私は',
    getInTouch: 'お問い合わせ',
    degrees: '学位',
    channels: 'チャンネル',
    technologiesDescription: '私が提供する技術と能力',
    letsConnect: 'つながりましょう',
    openToOpportunities: '新しい機会についていつでもお話しします',
    email: 'メール',
    phone: '電話',
    connectWithMe: '私とつながる',
    saveContact: '連絡先を保存',
    scanToSaveContact: 'スキャンして保存',
    createdWith: '作成',
    noDataAvailable: 'データがありません',
    noDataDescription: 'テンプレートを表示するには情報を追加してください。',
  },

  // Korean
  ko: {
    aboutMe: '자기소개',
    experiences: '경력',
    education: '학력',
    skills: '기술',
    technicalSkills: '기술적 능력',
    softSkills: '소프트 스킬',
    languages: '언어',
    contact: '연락처',
    websites: '링크',
    professionalJourney: '전문 경력',
    careerHighlights: '나의 경력 하이라이트와 성과',
    hiIm: '안녕하세요, 저는',
    getInTouch: '연락하기',
    degrees: '학위',
    channels: '채널',
    technologiesDescription: '제가 제공하는 기술과 역량',
    letsConnect: '연결하기',
    openToOpportunities: '새로운 기회에 대해 항상 열려 있습니다',
    email: '이메일',
    phone: '전화',
    connectWithMe: '저와 연결',
    saveContact: '연락처 저장',
    scanToSaveContact: '스캔하여 저장',
    createdWith: '만든 사람',
    noDataAvailable: '사용 가능한 데이터 없음',
    noDataDescription: '템플릿을 보려면 정보를 추가하세요.',
  },

  // Thai
  th: {
    aboutMe: 'เกี่ยวกับฉัน',
    experiences: 'ประสบการณ์การทำงาน',
    education: 'การศึกษา',
    skills: 'ทักษะ',
    technicalSkills: 'ทักษะทางเทคนิค',
    softSkills: 'ทักษะทางสังคม',
    languages: 'ภาษา',
    contact: 'ติดต่อ',
    websites: 'ลิงก์',
    professionalJourney: 'เส้นทางอาชีพ',
    careerHighlights: 'จุดเด่นและความสำเร็จในอาชีพของฉัน',
    hiIm: 'สวัสดี ฉันคือ',
    getInTouch: 'ติดต่อ',
    degrees: 'ปริญญา',
    channels: 'ช่องทาง',
    technologiesDescription: 'เทคโนโลยีและความสามารถที่ฉันนำมา',
    letsConnect: 'มาเชื่อมต่อกัน',
    openToOpportunities: 'ฉันเปิดรับโอกาสใหม่ๆ เสมอ',
    email: 'อีเมล',
    phone: 'โทรศัพท์',
    connectWithMe: 'เชื่อมต่อกับฉัน',
    saveContact: 'บันทึกผู้ติดต่อ',
    scanToSaveContact: 'สแกนเพื่อบันทึก',
    createdWith: 'สร้างด้วย',
    noDataAvailable: 'ไม่มีข้อมูล',
    noDataDescription: 'กรุณาเพิ่มข้อมูลของคุณเพื่อดูเทมเพลต',
  },

  // Vietnamese
  vi: {
    aboutMe: 'Về tôi',
    experiences: 'Kinh nghiệm làm việc',
    education: 'Học vấn',
    skills: 'Kỹ năng',
    technicalSkills: 'Kỹ năng kỹ thuật',
    softSkills: 'Kỹ năng mềm',
    languages: 'Ngôn ngữ',
    contact: 'Liên hệ',
    websites: 'Liên kết',
    professionalJourney: 'Hành trình nghề nghiệp',
    careerHighlights: 'Những điểm nổi bật và thành tựu trong sự nghiệp của tôi',
    hiIm: 'Xin chào, tôi là',
    getInTouch: 'Liên hệ',
    degrees: 'Bằng cấp',
    channels: 'Kênh',
    technologiesDescription: 'Công nghệ và năng lực tôi mang lại',
    letsConnect: 'Hãy kết nối',
    openToOpportunities: 'Tôi luôn sẵn sàng thảo luận về cơ hội mới',
    email: 'Email',
    phone: 'Điện thoại',
    connectWithMe: 'Kết nối với tôi',
    saveContact: 'Lưu liên hệ',
    scanToSaveContact: 'Quét để lưu',
    createdWith: 'Được tạo bởi',
    noDataAvailable: 'Không có dữ liệu',
    noDataDescription: 'Vui lòng thêm thông tin của bạn để xem mẫu.',
  },

  // Indonesian
  id: {
    aboutMe: 'Tentang Saya',
    experiences: 'Pengalaman Kerja',
    education: 'Pendidikan',
    skills: 'Keterampilan',
    technicalSkills: 'Keterampilan Teknis',
    softSkills: 'Keterampilan Lunak',
    languages: 'Bahasa',
    contact: 'Kontak',
    websites: 'Tautan',
    professionalJourney: 'Perjalanan profesional',
    careerHighlights: 'Sorotan dan pencapaian karir saya',
    hiIm: 'Hai, saya',
    getInTouch: 'Hubungi',
    degrees: 'Gelar',
    channels: 'Saluran',
    technologiesDescription: 'Teknologi dan kompetensi yang saya bawa',
    letsConnect: 'Mari terhubung',
    openToOpportunities: 'Saya selalu terbuka untuk membahas peluang baru',
    email: 'Email',
    phone: 'Telepon',
    connectWithMe: 'Hubungi saya',
    saveContact: 'Simpan kontak',
    scanToSaveContact: 'Pindai untuk menyimpan',
    createdWith: 'Dibuat dengan',
    noDataAvailable: 'Tidak ada data tersedia',
    noDataDescription: 'Silakan tambahkan informasi Anda untuk melihat template.',
  },

  // Malay
  ms: {
    aboutMe: 'Tentang Saya',
    experiences: 'Pengalaman Kerja',
    education: 'Pendidikan',
    skills: 'Kemahiran',
    technicalSkills: 'Kemahiran Teknikal',
    softSkills: 'Kemahiran Insaniah',
    languages: 'Bahasa',
    contact: 'Hubungi',
    websites: 'Pautan',
    professionalJourney: 'Perjalanan profesional',
    careerHighlights: 'Pencapaian dan kejayaan kerjaya saya',
    hiIm: 'Hai, saya',
    getInTouch: 'Hubungi',
    degrees: 'Ijazah',
    channels: 'Saluran',
    technologiesDescription: 'Teknologi dan kecekapan yang saya tawarkan',
    letsConnect: 'Mari berhubung',
    openToOpportunities: 'Saya sentiasa terbuka untuk membincangkan peluang baru',
    email: 'E-mel',
    phone: 'Telefon',
    connectWithMe: 'Hubungi saya',
    saveContact: 'Simpan kenalan',
    scanToSaveContact: 'Imbas untuk simpan',
    createdWith: 'Dicipta dengan',
    noDataAvailable: 'Tiada data tersedia',
    noDataDescription: 'Sila tambah maklumat anda untuk melihat templat.',
  },

  // Hindi
  hi: {
    aboutMe: 'मेरे बारे में',
    experiences: 'कार्य अनुभव',
    education: 'शिक्षा',
    skills: 'कौशल',
    technicalSkills: 'तकनीकी कौशल',
    softSkills: 'सॉफ्ट स्किल्स',
    languages: 'भाषाएँ',
    contact: 'संपर्क',
    websites: 'लिंक',
    professionalJourney: 'व्यावसायिक यात्रा',
    careerHighlights: 'मेरी करियर की मुख्य बातें और उपलब्धियां',
    hiIm: 'नमस्ते, मैं हूं',
    getInTouch: 'संपर्क करें',
    degrees: 'डिग्री',
    channels: 'चैनल',
    technologiesDescription: 'प्रौद्योगिकियां और दक्षताएं जो मैं लाता हूं',
    letsConnect: 'आइए जुड़ें',
    openToOpportunities: 'मैं नए अवसरों पर चर्चा के लिए हमेशा तैयार हूं',
    email: 'ईमेल',
    phone: 'फोन',
    connectWithMe: 'मुझसे जुड़ें',
    saveContact: 'संपर्क सहेजें',
    scanToSaveContact: 'सहेजने के लिए स्कैन करें',
    createdWith: 'के साथ बनाया गया',
    noDataAvailable: 'कोई डेटा उपलब्ध नहीं',
    noDataDescription: 'कृपया टेम्पलेट देखने के लिए अपनी जानकारी जोड़ें।',
  },

  // Bengali
  bn: {
    aboutMe: 'আমার সম্পর্কে',
    experiences: 'কাজের অভিজ্ঞতা',
    education: 'শিক্ষা',
    skills: 'দক্ষতা',
    technicalSkills: 'প্রযুক্তিগত দক্ষতা',
    softSkills: 'সফট স্কিল',
    languages: 'ভাষা',
    contact: 'যোগাযোগ',
    websites: 'লিংক',
    professionalJourney: 'পেশাদার যাত্রা',
    careerHighlights: 'আমার ক্যারিয়ারের হাইলাইট এবং অর্জন',
    hiIm: 'হাই, আমি',
    getInTouch: 'যোগাযোগ করুন',
    degrees: 'ডিগ্রি',
    channels: 'চ্যানেল',
    technologiesDescription: 'প্রযুক্তি এবং দক্ষতা যা আমি নিয়ে আসি',
    letsConnect: 'চলুন সংযোগ করি',
    openToOpportunities: 'আমি সবসময় নতুন সুযোগ নিয়ে আলোচনার জন্য প্রস্তুত',
    email: 'ইমেইল',
    phone: 'ফোন',
    connectWithMe: 'আমার সাথে যোগাযোগ করুন',
    saveContact: 'যোগাযোগ সংরক্ষণ করুন',
    scanToSaveContact: 'সংরক্ষণ করতে স্ক্যান করুন',
    createdWith: 'দিয়ে তৈরি',
    noDataAvailable: 'কোন ডেটা উপলব্ধ নেই',
    noDataDescription: 'টেমপ্লেট দেখতে আপনার তথ্য যোগ করুন।',
  },

  // Urdu
  ur: {
    aboutMe: 'میرے بارے میں',
    experiences: 'کام کا تجربہ',
    education: 'تعلیم',
    skills: 'مہارتیں',
    technicalSkills: 'تکنیکی مہارتیں',
    softSkills: 'نرم مہارتیں',
    languages: 'زبانیں',
    contact: 'رابطہ',
    websites: 'روابط',
    professionalJourney: 'پیشہ ورانہ سفر',
    careerHighlights: 'میری کیریئر کی اہم کامیابیاں',
    hiIm: 'ہیلو، میں ہوں',
    getInTouch: 'رابطہ کریں',
    degrees: 'ڈگریاں',
    channels: 'چینلز',
    technologiesDescription: 'ٹیکنالوجی اور مہارتیں جو میں پیش کرتا ہوں',
    letsConnect: 'آئیے جڑیں',
    openToOpportunities: 'میں ہمیشہ نئے مواقع پر بات کرنے کے لیے تیار ہوں',
    email: 'ای میل',
    phone: 'فون',
    connectWithMe: 'مجھ سے رابطہ کریں',
    saveContact: 'رابطہ محفوظ کریں',
    scanToSaveContact: 'محفوظ کرنے کے لیے اسکین کریں',
    createdWith: 'کے ساتھ بنایا گیا',
    noDataAvailable: 'کوئی ڈیٹا دستیاب نہیں',
    noDataDescription: 'براہ کرم ٹیمپلیٹ دیکھنے کے لیے اپنی معلومات شامل کریں۔',
  },

  // Arabic
  ar: {
    aboutMe: 'عني',
    experiences: 'الخبرة العملية',
    education: 'التعليم',
    skills: 'المهارات',
    technicalSkills: 'المهارات التقنية',
    softSkills: 'المهارات الشخصية',
    languages: 'اللغات',
    contact: 'اتصل',
    websites: 'الروابط',
    professionalJourney: 'الرحلة المهنية',
    careerHighlights: 'أبرز إنجازاتي المهنية ونجاحاتي',
    hiIm: 'مرحبًا، أنا',
    getInTouch: 'تواصل معي',
    degrees: 'الدرجات',
    channels: 'القنوات',
    technologiesDescription: 'التقنيات والكفاءات التي أقدمها',
    letsConnect: 'لنتواصل',
    openToOpportunities: 'أنا دائمًا منفتح لمناقشة الفرص الجديدة',
    email: 'البريد الإلكتروني',
    phone: 'الهاتف',
    connectWithMe: 'تواصل معي',
    saveContact: 'حفظ جهة الاتصال',
    scanToSaveContact: 'امسح للحفظ',
    createdWith: 'تم الإنشاء باستخدام',
    noDataAvailable: 'لا توجد بيانات متاحة',
    noDataDescription: 'يرجى إضافة معلوماتك لمشاهدة القالب.',
  },

  // Hebrew
  he: {
    aboutMe: 'עליי',
    experiences: 'ניסיון תעסוקתי',
    education: 'השכלה',
    skills: 'כישורים',
    technicalSkills: 'כישורים טכניים',
    softSkills: 'כישורים רכים',
    languages: 'שפות',
    contact: 'יצירת קשר',
    websites: 'קישורים',
    professionalJourney: 'המסע המקצועי',
    careerHighlights: 'שיאי הקריירה וההישגים שלי',
    hiIm: 'שלום, אני',
    getInTouch: 'צור קשר',
    degrees: 'תארים',
    channels: 'ערוצים',
    technologiesDescription: 'טכנולוגיות ויכולות שאני מביא',
    letsConnect: 'בוא נתחבר',
    openToOpportunities: 'אני תמיד פתוח לדון בהזדמנויות חדשות',
    email: 'דוא"ל',
    phone: 'טלפון',
    connectWithMe: 'התחבר אליי',
    saveContact: 'שמור איש קשר',
    scanToSaveContact: 'סרוק לשמירה',
    createdWith: 'נוצר באמצעות',
    noDataAvailable: 'אין נתונים זמינים',
    noDataDescription: 'אנא הוסף את המידע שלך כדי לראות את התבנית.',
  },

  // Persian
  fa: {
    aboutMe: 'درباره من',
    experiences: 'تجربه کاری',
    education: 'تحصیلات',
    skills: 'مهارت‌ها',
    technicalSkills: 'مهارت‌های فنی',
    softSkills: 'مهارت‌های نرم',
    languages: 'زبان‌ها',
    contact: 'تماس',
    websites: 'لینک‌ها',
    professionalJourney: 'سفر حرفه‌ای',
    careerHighlights: 'نقاط برجسته و دستاوردهای شغلی من',
    hiIm: 'سلام، من هستم',
    getInTouch: 'تماس بگیرید',
    degrees: 'مدارک',
    channels: 'کانال‌ها',
    technologiesDescription: 'فناوری‌ها و مهارت‌هایی که ارائه می‌دهم',
    letsConnect: 'بیایید متصل شویم',
    openToOpportunities: 'من همیشه برای بحث درباره فرصت‌های جدید آماده هستم',
    email: 'ایمیل',
    phone: 'تلفن',
    connectWithMe: 'با من ارتباط برقرار کنید',
    saveContact: 'ذخیره مخاطب',
    scanToSaveContact: 'برای ذخیره اسکن کنید',
    createdWith: 'ایجاد شده با',
    noDataAvailable: 'داده‌ای در دسترس نیست',
    noDataDescription: 'لطفاً اطلاعات خود را برای مشاهده قالب اضافه کنید.',
  },

  // Spanish variants
  'es-MX': {
    aboutMe: 'Acerca de mí',
    experiences: 'Experiencia laboral',
    education: 'Educación',
    skills: 'Habilidades',
    technicalSkills: 'Habilidades técnicas',
    softSkills: 'Habilidades blandas',
    languages: 'Idiomas',
    contact: 'Contacto',
    websites: 'Enlaces',
    professionalJourney: 'Professional Journey',
    careerHighlights: 'Hlavní úspěchy a milníky kariéry',
    hiIm: 'Hola, soy',
    getInTouch: 'Ponte en contacto',
    degrees: 'Títulos',
    channels: 'Canales',
    technologiesDescription: 'Tecnologías y competencias que aporto',
    letsConnect: 'Conectemos',
    openToOpportunities: 'Siempre estoy abierto a discutir nuevas oportunidades',
    email: 'Correo',
    phone: 'Teléfono',
    connectWithMe: 'Conéctate conmigo',
    saveContact: 'Guardar contacto',
    scanToSaveContact: 'Escanea para guardar',
    createdWith: 'Creado con',
    noDataAvailable: 'No hay datos disponibles',
    noDataDescription: 'Por favor agrega tu información para ver la plantilla.',
    customSection: 'Sección personalizada',
    present: 'Presente',
  },

  'es-AR': {
    aboutMe: 'Sobre mí',
    experiences: 'Experiencia laboral',
    education: 'Educación',
    skills: 'Habilidades',
    technicalSkills: 'Habilidades técnicas',
    softSkills: 'Habilidades blandas',
    languages: 'Idiomas',
    contact: 'Contacto',
    websites: 'Enlaces',
    professionalJourney: 'Professional Journey',
    careerHighlights: 'Hlavní úspěchy a milníky kariéry',
    hiIm: 'Hola, soy',
    getInTouch: 'Contactame',
    degrees: 'Títulos',
    channels: 'Canales',
    technologiesDescription: 'Tecnologías y competencias que aporto',
    letsConnect: 'Conectemos',
    openToOpportunities: 'Siempre estoy abierto a discutir nuevas oportunidades',
    email: 'Correo',
    phone: 'Teléfono',
    connectWithMe: 'Conectate conmigo',
    saveContact: 'Guardar contacto',
    scanToSaveContact: 'Escanear para guardar',
    createdWith: 'Creado con',
    noDataAvailable: 'No hay datos disponibles',
    noDataDescription: 'Por favor agregá tu información para ver la plantilla.',
  },

  // English variants
  'en-US': {
    aboutMe: 'About Me',
    experiences: 'Work Experience',
    education: 'Education',
    skills: 'Skills',
    technicalSkills: 'Technical Skills',
    softSkills: 'Soft Skills',
    languages: 'Languages',
    contact: 'Contact',
    websites: 'Links',
    professionalJourney: 'Professional Journey',
    careerHighlights: 'Hlavní úspěchy a milníky kariéry',
    hiIm: 'Hi, I\'m',
    getInTouch: 'Get in Touch',
    degrees: 'Degrees',
    channels: 'Channels',
    technologiesDescription: 'Technologies and competencies I bring to the table',
    letsConnect: 'Let\'s Connect',
    openToOpportunities: 'I\'m always open to discussing new opportunities',
    email: 'Email',
    phone: 'Phone',
    connectWithMe: 'Connect with me',
    saveContact: 'Save Contact',
    scanToSaveContact: 'Scan to save contact',
    createdWith: 'Created with',
    noDataAvailable: 'No Data Available',
    noDataDescription: 'Please add your information to see the business web template.',
  },

  'en-GB': {
    aboutMe: 'About Me',
    experiences: 'Work Experience',
    education: 'Education',
    skills: 'Skills',
    technicalSkills: 'Technical Skills',
    softSkills: 'Soft Skills',
    languages: 'Languages',
    contact: 'Contact',
    websites: 'Links',
    professionalJourney: 'Professional Journey',
    careerHighlights: 'Hlavní úspěchy a milníky kariéry',
    hiIm: 'Hi, I\'m',
    getInTouch: 'Get in Touch',
    degrees: 'Degrees',
    channels: 'Channels',
    technologiesDescription: 'Technologies and competencies I bring to the table',
    letsConnect: 'Let\'s Connect',
    openToOpportunities: 'I\'m always open to discussing new opportunities',
    email: 'Email',
    phone: 'Phone',
    connectWithMe: 'Connect with me',
    saveContact: 'Save Contact',
    scanToSaveContact: 'Scan to save contact',
    createdWith: 'Created with',
    noDataAvailable: 'No Data Available',
    noDataDescription: 'Please add your information to see the business web template.',
  },

  'en-AU': {
    aboutMe: 'About Me',
    experiences: 'Work Experience',
    education: 'Education',
    skills: 'Skills',
    technicalSkills: 'Technical Skills',
    softSkills: 'Soft Skills',
    languages: 'Languages',
    contact: 'Contact',
    websites: 'Links',
    professionalJourney: 'Professional Journey',
    careerHighlights: 'Hlavní úspěchy a milníky kariéry',
    hiIm: 'G\'day, I\'m',
    getInTouch: 'Get in Touch',
    degrees: 'Degrees',
    channels: 'Channels',
    technologiesDescription: 'Technologies and competencies I bring to the table',
    letsConnect: 'Let\'s Connect',
    openToOpportunities: 'I\'m always open to discussing new opportunities',
    email: 'Email',
    phone: 'Phone',
    connectWithMe: 'Connect with me',
    saveContact: 'Save Contact',
    scanToSaveContact: 'Scan to save contact',
    createdWith: 'Created with',
    noDataAvailable: 'No Data Available',
    noDataDescription: 'Please add your information to see the business web template.',
  },

  'en-CA': {
    aboutMe: 'About Me',
    experiences: 'Work Experience',
    education: 'Education',
    skills: 'Skills',
    technicalSkills: 'Technical Skills',
    softSkills: 'Soft Skills',
    languages: 'Languages',
    contact: 'Contact',
    websites: 'Links',
    professionalJourney: 'Professional Journey',
    careerHighlights: 'Hlavní úspěchy a milníky kariéry',
    hiIm: 'Hi, I\'m',
    getInTouch: 'Get in Touch',
    degrees: 'Degrees',
    channels: 'Channels',
    technologiesDescription: 'Technologies and competencies I bring to the table',
    letsConnect: 'Let\'s Connect',
    openToOpportunities: 'I\'m always open to discussing new opportunities',
    email: 'Email',
    phone: 'Phone',
    connectWithMe: 'Connect with me',
    saveContact: 'Save Contact',
    scanToSaveContact: 'Scan to save contact',
    createdWith: 'Created with',
    noDataAvailable: 'No Data Available',
    noDataDescription: 'Please add your information to see the business web template.',
  },

  'fr-CA': {
    aboutMe: 'À propos de moi',
    experiences: 'Expérience professionnelle',
    education: 'Formation',
    skills: 'Compétences',
    technicalSkills: 'Compétences techniques',
    softSkills: 'Compétences interpersonnelles',
    languages: 'Langues',
    contact: 'Contact',
    websites: 'Liens',
    professionalJourney: 'Professional Journey',
    careerHighlights: 'Hlavní úspěchy a milníky kariéry',
    hiIm: 'Bonjour, je suis',
    getInTouch: 'Contactez-moi',
    degrees: 'Diplômes',
    channels: 'Canaux',
    technologiesDescription: 'Technologies et compétences que j\'apporte',
    letsConnect: 'Connectons-nous',
    openToOpportunities: 'Je suis toujours ouvert à discuter de nouvelles opportunités',
    email: 'Courriel',
    phone: 'Téléphone',
    connectWithMe: 'Connectez-vous avec moi',
    saveContact: 'Enregistrer le contact',
    scanToSaveContact: 'Numériser pour enregistrer',
    createdWith: 'Créé avec',
    noDataAvailable: 'Aucune donnée disponible',
    noDataDescription: 'Veuillez ajouter vos informations pour voir le modèle.',
  },

  // African languages
  sw: {
    aboutMe: 'Kuhusu Mimi',
    experiences: 'Uzoefu wa Kazi',
    education: 'Elimu',
    skills: 'Ujuzi',
    technicalSkills: 'Ujuzi wa Kiufundi',
    softSkills: 'Ujuzi Laini',
    languages: 'Lugha',
    contact: 'Mawasiliano',
    websites: 'Viungo',
    professionalJourney: 'Safari ya kitaaluma',
    careerHighlights: 'Mafanikio na mafanikio yangu ya kazi',
    hiIm: 'Habari, mimi ni',
    getInTouch: 'Wasiliana',
    degrees: 'Digrii',
    channels: 'Njia',
    technologiesDescription: 'Teknolojia na uwezo ninaoleta',
    letsConnect: 'Hebu tuunganishe',
    openToOpportunities: 'Daima niko tayari kujadili fursa mpya',
    email: 'Barua pepe',
    phone: 'Simu',
    connectWithMe: 'Unganisha nami',
    saveContact: 'Hifadhi mawasiliano',
    scanToSaveContact: 'Changanua ili uhifadhi',
    createdWith: 'Imeundwa na',
    noDataAvailable: 'Hakuna data',
    noDataDescription: 'Tafadhali ongeza taarifa zako ili kuona kiolezo.',
  },

  af: {
    aboutMe: 'Oor my',
    experiences: 'Werkservaring',
    education: 'Opvoeding',
    skills: 'Vaardighede',
    technicalSkills: 'Tegniese vaardighede',
    softSkills: 'Sagte vaardighede',
    languages: 'Tale',
    contact: 'Kontak',
    websites: 'Skakels',
    professionalJourney: 'Professionele reis',
    careerHighlights: 'My loopbaanhoogtepunte en prestasies',
    hiIm: 'Hallo, ek is',
    getInTouch: 'Kontak my',
    degrees: 'Grade',
    channels: 'Kanale',
    technologiesDescription: 'Tegnologieë en bevoegdhede wat ek bring',
    letsConnect: 'Kom ons verbind',
    openToOpportunities: 'Ek is altyd oop om nuwe geleenthede te bespreek',
    email: 'E-pos',
    phone: 'Telefoon',
    connectWithMe: 'Verbind met my',
    saveContact: 'Stoor kontak',
    scanToSaveContact: 'Skandeer om te stoor',
    createdWith: 'Geskep met',
    noDataAvailable: 'Geen data beskikbaar nie',
    noDataDescription: 'Voeg asseblief jou inligting by om die sjabloon te sien.',
  },

  am: {
    aboutMe: 'ስለ እኔ',
    experiences: 'የስራ ልምድ',
    education: 'ትምህርት',
    skills: 'ክህሎቶች',
    technicalSkills: 'ቴክኒካዊ ክህሎቶች',
    softSkills: 'ለስላሳ ክህሎቶች',
    languages: 'ቋንቋዎች',
    contact: 'ግንኙነት',
    websites: 'አገናኞች',
    professionalJourney: 'ሙያዊ ጉዞ',
    careerHighlights: 'የስራ ዕድገቴ ዋና ዋና ነጥቦች እና ስኬቶች',
    hiIm: 'ሰላም፣ እኔ ነኝ',
    getInTouch: 'ያግኙን',
    degrees: 'ዲግሪዎች',
    channels: 'ቻናሎች',
    technologiesDescription: 'የማምጣቸው ቴክኖሎጂዎች እና ችሎታዎች',
    letsConnect: 'እንገናኝ',
    openToOpportunities: 'ሁልጊዜ አዳዲስ እድሎችን ለመወያየት ክፍት ነኝ',
    email: 'ኢሜይል',
    phone: 'ስልክ',
    connectWithMe: 'ከእኔ ጋር ተገናኝ',
    saveContact: 'ግንኙነት ያስቀምጡ',
    scanToSaveContact: 'ለማስቀመጥ ስካን ያድርጉ',
    createdWith: 'የተፈጠረው በ',
    noDataAvailable: 'ምንም መረጃ የለም',
    noDataDescription: 'እባክዎ ቅጹን ለማየት መረጃዎን ያክሉ።',
  },

  // Additional European
  is: {
    aboutMe: 'Um mig',
    experiences: 'Starfsreynsla',
    education: 'Menntun',
    skills: 'Færni',
    technicalSkills: 'Tæknileg færni',
    softSkills: 'Mjúk færni',
    languages: 'Tungumál',
    contact: 'Samband',
    websites: 'Tenglar',
    professionalJourney: 'Fagleg ferð',
    careerHighlights: 'Hápunktar og árangur ferils míns',
    hiIm: 'Hæ, ég er',
    getInTouch: 'Hafðu samband',
    degrees: 'Gráður',
    channels: 'Rásir',
    technologiesDescription: 'Tækni og hæfni sem ég kem með',
    letsConnect: 'Komum okkur saman',
    openToOpportunities: 'Ég er alltaf opinn fyrir að ræða ný tækifæri',
    email: 'Tölvupóstur',
    phone: 'Sími',
    connectWithMe: 'Tengdust mér',
    saveContact: 'Vista tengilið',
    scanToSaveContact: 'Skannaðu til að vista',
    createdWith: 'Búið til með',
    noDataAvailable: 'Engin gögn tiltæk',
    noDataDescription: 'Vinsamlegast bættu við upplýsingum þínum til að sjá sniðmátið.',
  },

  ga: {
    aboutMe: 'Fúm',
    experiences: 'Taithí Oibre',
    education: 'Oideachas',
    skills: 'Scileanna',
    technicalSkills: 'Scileanna Teicniúla',
    softSkills: 'Scileanna Boga',
    languages: 'Teangacha',
    contact: 'Teagmháil',
    websites: 'Naisc',
    professionalJourney: 'Turas gairmiúil',
    careerHighlights: 'Buaicphointí agus éachtaí mo ghairme',
    hiIm: 'Dia duit, is mise',
    getInTouch: 'Déan teagmháil',
    degrees: 'Céimeanna',
    channels: 'Bealaí',
    technologiesDescription: 'Teicneolaíochtaí agus inniúlachtaí a thugaim',
    letsConnect: 'Déanaimis ceangal',
    openToOpportunities: 'Táim i gcónaí oscailte chun deiseanna nua a phlé',
    email: 'Ríomhphost',
    phone: 'Fón',
    connectWithMe: 'Ceangail liom',
    saveContact: 'Sábháil teagmháil',
    scanToSaveContact: 'Scan chun sábháil',
    createdWith: 'Cruthaithe le',
    noDataAvailable: 'Níl aon sonraí ar fáil',
    noDataDescription: 'Cuir d\'eolas leis le do thoil chun an teimpléad a fheiceáil.',
  },

  mt: {
    aboutMe: 'Dwari',
    experiences: 'Esperjenza tax-Xogħol',
    education: 'Edukazzjoni',
    skills: 'Ħiliet',
    technicalSkills: 'Ħiliet Tekniċi',
    softSkills: 'Ħiliet Soċjali',
    languages: 'Lingwi',
    contact: 'Kuntatt',
    websites: 'Links',
    professionalJourney: 'Vjaġġ professjonali',
    careerHighlights: 'Il-punti kul me tal-karriera u l-kisb tiegħi',
    hiIm: 'Bonġu, jien',
    getInTouch: 'Ikkuntattja',
    degrees: 'Gradi',
    channels: 'Kanali',
    technologiesDescription: 'Teknoloġiji u kompetenzi li nġib',
    letsConnect: 'Ejja nikkonnettjaw',
    openToOpportunities: 'Dejjem ninsab miftuħ biex niddiskuti opportunitajiet ġodda',
    email: 'Email',
    phone: 'Telefon',
    connectWithMe: 'Ikkonnettja miegħi',
    saveContact: 'Salva kuntatt',
    scanToSaveContact: 'Skannja biex issalva',
    createdWith: 'Maħluq b\'',
    noDataAvailable: 'Ebda data disponibbli',
    noDataDescription: 'Jekk jogħġbok żid l-informazzjoni tiegħek biex tara t-template.',
  },

  sq: {
    aboutMe: 'Rreth meje',
    experiences: 'Përvoja e punës',
    education: 'Arsimi',
    skills: 'Aftësitë',
    technicalSkills: 'Aftësitë teknike',
    softSkills: 'Aftësitë e buta',
    languages: 'Gjuhët',
    contact: 'Kontakti',
    websites: 'Lidhjet',
    professionalJourney: 'Udhëtimi profesional',
    careerHighlights: 'Arritjet dhe sukseset e mia në karrierë',
    hiIm: 'Përshëndetje, unë jam',
    getInTouch: 'Kontaktoni',
    degrees: 'Gradë',
    channels: 'Kanale',
    technologiesDescription: 'Teknologji dhe kompetenca që sjell',
    letsConnect: 'Le të lidhemi',
    openToOpportunities: 'Jam gjithmonë i hapur për të diskutuar mundësi të reja',
    email: 'Email',
    phone: 'Telefon',
    connectWithMe: 'Lidhu me mua',
    saveContact: 'Ruaj kontaktin',
    scanToSaveContact: 'Skanoni për të ruajtur',
    createdWith: 'Krijuar me',
    noDataAvailable: 'Nuk ka të dhëna',
    noDataDescription: 'Ju lutemi shtoni informacionin tuaj për të parë shabllonin.',
  },

  mk: {
    aboutMe: 'За мене',
    experiences: 'Работно искуство',
    education: 'Образование',
    skills: 'Вештини',
    technicalSkills: 'Технички вештини',
    softSkills: 'Меки вештини',
    languages: 'Јазици',
    contact: 'Контакт',
    websites: 'Врски',
    professionalJourney: 'Професионален пат',
    careerHighlights: 'Моите кариерни достигнувања и успеси',
    hiIm: 'Здраво, јас сум',
    getInTouch: 'Контактирајте',
    degrees: 'Степени',
    channels: 'Канали',
    technologiesDescription: 'Технологии и компетенции што ги носам',
    letsConnect: 'Да се поврземе',
    openToOpportunities: 'Секогаш сум отворен за дискусија за нови можности',
    email: 'Е-пошта',
    phone: 'Телефон',
    connectWithMe: 'Поврзете се со мене',
    saveContact: 'Зачувај контакт',
    scanToSaveContact: 'Скенирај за зачувување',
    createdWith: 'Создадено со',
    noDataAvailable: 'Нема достапни податоци',
    noDataDescription: 'Ве молиме додадете ги вашите информации за да го видите шаблонот.',
  },

  bs: {
    aboutMe: 'O meni',
    experiences: 'Radno iskustvo',
    education: 'Obrazovanje',
    skills: 'Vještine',
    technicalSkills: 'Tehničke vještine',
    softSkills: 'Meke vještine',
    languages: 'Jezici',
    contact: 'Kontakt',
    websites: 'Linkovi',
    professionalJourney: 'Profesionalni put',
    careerHighlights: 'Moji karijerni uspjesi i dostignuća',
    hiIm: 'Zdravo, ja sam',
    getInTouch: 'Kontaktirajte',
    degrees: 'Diplome',
    channels: 'Kanali',
    technologiesDescription: 'Tehnologije i kompetencije koje donosim',
    letsConnect: 'Povežimo se',
    openToOpportunities: 'Uvijek sam otvoren za razgovor o novim prilikama',
    email: 'Email',
    phone: 'Telefon',
    connectWithMe: 'Povežite se sa mnom',
    saveContact: 'Spremi kontakt',
    scanToSaveContact: 'Skeniraj za spremanje',
    createdWith: 'Kreirano sa',
    noDataAvailable: 'Nema dostupnih podataka',
    noDataDescription: 'Molimo dodajte vaše podatke da vidite predložak.',
  },

  cy: {
    aboutMe: 'Amdanaf',
    experiences: 'Profiad Gwaith',
    education: 'Addysg',
    skills: 'Sgiliau',
    technicalSkills: 'Sgiliau Technegol',
    softSkills: 'Sgiliau Meddal',
    languages: 'Ieithoedd',
    contact: 'Cyswllt',
    websites: 'Dolenni',
    professionalJourney: 'Taith broffesiynol',
    careerHighlights: 'Uchafbwyntiau a chyflawniadau fy ngyrfa',
    hiIm: 'Helo, fi yw',
    getInTouch: 'Cysylltwch',
    degrees: 'Graddau',
    channels: 'Sianeli',
    technologiesDescription: 'Technolegau a chymwyseddau rwy\'n eu cynnig',
    letsConnect: 'Gadewch i ni gysylltu',
    openToOpportunities: 'Rwy\'n agored bob amser i drafod cyfleoedd newydd',
    email: 'E-bost',
    phone: 'Ffôn',
    connectWithMe: 'Cysylltwch â mi',
    saveContact: 'Arbed cyswllt',
    scanToSaveContact: 'Sganio i arbed',
    createdWith: 'Crëwyd gyda',
    noDataAvailable: 'Dim data ar gael',
    noDataDescription: 'Ychwanegwch eich gwybodaeth i weld y templed.',
  },

  eu: {
    aboutMe: 'Niri buruz',
    experiences: 'Lan esperientzia',
    education: 'Hezkuntza',
    skills: 'Gaitasunak',
    technicalSkills: 'Gaitasun teknikoak',
    softSkills: 'Gaitasun gizatiarrak',
    languages: 'Hizkuntzak',
    contact: 'Kontaktua',
    websites: 'Estekak',
    professionalJourney: 'Ibilbide profesionala',
    careerHighlights: 'Nire ibilbidearen mugarriak eta lorpenak',
    hiIm: 'Kaixo, ni naiz',
    getInTouch: 'Jarri harremanetan',
    degrees: 'Graduak',
    channels: 'Kanalak',
    technologiesDescription: 'Ekartzen ditudan teknologiak eta gaitasunak',
    letsConnect: 'Konektatu gaitezen',
    openToOpportunities: 'Beti nago aukera berriak eztabaidatzeko prest',
    email: 'E-posta',
    phone: 'Telefonoa',
    connectWithMe: 'Konektatu nirekin',
    saveContact: 'Gorde kontaktua',
    scanToSaveContact: 'Eskaneatu gordetzeko',
    createdWith: 'Honekin sortua',
    noDataAvailable: 'Ez dago daturik eskuragarri',
    noDataDescription: 'Mesedez gehitu zure informazioa txantiloia ikusteko.',
  },

  ca: {
    aboutMe: 'Sobre mi',
    experiences: 'Experiència laboral',
    education: 'Educació',
    skills: 'Habilitats',
    technicalSkills: 'Habilitats tècniques',
    softSkills: 'Habilitats interpersonals',
    languages: 'Idiomes',
    contact: 'Contacte',
    websites: 'Enllaços',
    professionalJourney: 'Trajectòria professional',
    careerHighlights: 'Els meus èxits i assoliments professionals',
    hiIm: 'Hola, sóc',
    getInTouch: 'Posa\'t en contacte',
    degrees: 'Títols',
    channels: 'Canals',
    technologiesDescription: 'Tecnologies i competències que aporto',
    letsConnect: 'Connectem',
    openToOpportunities: 'Sempre estic obert a discutir noves oportunitats',
    email: 'Correu',
    phone: 'Telèfon',
    connectWithMe: 'Connecta amb mi',
    saveContact: 'Desa contacte',
    scanToSaveContact: 'Escaneja per desar',
    createdWith: 'Creat amb',
    noDataAvailable: 'No hi ha dades disponibles',
    noDataDescription: 'Si us plau afegeix la teva informació per veure la plantilla.',
  },

  gl: {
    aboutMe: 'Sobre min',
    experiences: 'Experiencia laboral',
    education: 'Educación',
    skills: 'Habilidades',
    technicalSkills: 'Habilidades técnicas',
    softSkills: 'Habilidades brandas',
    languages: 'Idiomas',
    contact: 'Contacto',
    websites: 'Ligazóns',
    professionalJourney: 'Traxectoria profesional',
    careerHighlights: 'Os meus logros e éxitos profesionais',
    hiIm: 'Ola, son',
    getInTouch: 'Ponte en contacto',
    degrees: 'Títulos',
    channels: 'Canles',
    technologiesDescription: 'Tecnoloxías e competencias que achego',
    letsConnect: 'Conectemos',
    openToOpportunities: 'Sempre estou aberto a discutir novas oportunidades',
    email: 'Correo',
    phone: 'Teléfono',
    connectWithMe: 'Conecta comigo',
    saveContact: 'Gardar contacto',
    scanToSaveContact: 'Escanea para gardar',
    createdWith: 'Creado con',
    noDataAvailable: 'Non hai datos dispoñibles',
    noDataDescription: 'Por favor engade a súa información para ver o modelo.',
  },
}

/**
 * Get CV section translations for a specific language
 * Falls back to English if language not found
 */
export function getCVSectionTranslations(languageCode: string): Required<CVSectionTranslations> {
  const base = languageCode?.split('-')[0] ?? ''
  const selected = CV_SECTION_TRANSLATIONS[languageCode] || CV_SECTION_TRANSLATIONS[base] || {}
  const nativeSpeaker = NATIVE_SPEAKER[languageCode] || NATIVE_SPEAKER[base] || NATIVE_SPEAKER.en!
  // Spread over English so keys missing in a language never render as undefined
  return { ...CV_SECTION_TRANSLATIONS.en!, ...selected, nativeSpeaker } as Required<CVSectionTranslations>
}
